/**
 * 화면 오류 경계
 * ---------------------------------------------------------------------------
 * React 는 렌더 도중 예외가 나면 트리 전체를 떼어 낸다. 경계가 없으면 그
 * 결과가 **하얀 빈 화면**이다. 사용자는 "눌러도 아무것도 안 나온다" 고만
 * 말할 수 있고, 개발자 도구를 열지 않는 한 아무 단서도 남지 않는다.
 *
 * 무엇이 잘못됐는지 화면에 적고, 다시 시도할 길을 준다. 오류 내용도 접어서
 * 보여 준다 — 현장에서 전화로 읽어 줄 수 있어야 원인을 찾는다.
 *
 * 되돌리는 방법
 *   같은 화면을 다시 그리면 대개 같은 자리에서 또 터진다. 상태를 비우고
 *   다시 그려 보되, 그래도 안 되면 목록으로 나갈 수 있게 한다.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result, Typography } from 'antd';

interface Props {
  children: ReactNode;
  /** 이 값이 바뀌면 오류 상태를 푼다. 보통 현재 경로를 넣는다. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  resetKey?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  /** 다른 화면으로 옮기면 오류를 푼다. 한 번 터진 뒤 앱 전체가 멈추면 안 된다. */
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.resetKey !== props.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 콘솔에는 남겨 둔다. 개발 중에는 이것이 가장 빠른 단서다.
    console.error('[WORK&ECO] 화면 오류', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Result
        status="error"
        title="화면을 표시하지 못했습니다"
        subTitle="작성 중이던 내용은 저장되지 않았을 수 있습니다. 다시 시도해 주세요."
        extra={[
          <Button key="retry" type="primary" onClick={() => this.setState({ error: null })}>
            다시 시도
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>
            새로고침
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <details>
            <summary style={{ cursor: 'pointer' }}>오류 내용</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 8 }}>
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        </Typography.Paragraph>
      </Result>
    );
  }
}
